import React from 'react';

import type { CompositionV2DataQuery } from '@revelio/data-access';
import {
  type BreadcrumbSegment,
  getOtherColor,
  getPrimaryColor,
  TreeMap as ReplotsTreeMap,
  type TreeMapData,
} from '@revelio/replots';

import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import {
  getTreemapDrilldownIsLeafLevel,
  getTreemapDrilldownLevelOrder,
  getTreemapInitialLevelOffset,
  isTreemapDrilldownSegment,
  type TreemapDrilldownSegment,
} from '../../filters/utils/treemapDrilldownLevels';
import { usePlotMetric } from '../../hooks/usePlotMetric';
import {
  CompositionTreemapDrilldownProvider,
  type DrilldownFilter,
} from '../DataProvider/composition/CompositionTreemapDrilldownProvider';
import {
  type BreakdownSingleDataSet,
  DataProviderQueryStatus,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
} from '../DataProvider/types';
import { convertCompositionData } from '../DataProvider/utils/convert-composition-data';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType } from '../PageConfigProvider/types';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import { ChartLoader } from './ChartLoader';
import { useChart } from './ChartProvider';
import { SharableChart } from './chart-menu/plot-share/SharableChart';
import { getFormat } from './utils/getFormat';

type DrilldownEntry = {
  level: string;
  id: number | string;
  name: string;
  segment: TreemapDrilldownSegment;
};

function drilldownEntryToFilter(entry: DrilldownEntry): DrilldownFilter {
  switch (entry.segment) {
    case 'geography':
      return {
        segment: 'geography',
        level: entry.level as 'region' | 'country' | 'msa' | 'city',
        id: Number(entry.id),
      };
    case 'role':
      return {
        segment: 'role',
        levelId: entry.level as
          | 'role_k7'
          | 'role_k50'
          | 'role_k150'
          | 'role_k500'
          | 'role_k1500'
          | 'role_k5000'
          | 'role_k15000',
        id: String(entry.id),
      };
    case 'rics':
      return {
        segment: 'rics',
        level: entry.level as 'rics_k10' | 'rics_k50' | 'rics_k400' | 'company',
        id: Number(entry.id),
      };
    case 'skills':
      return {
        segment: 'skills',
        level: entry.level as
          | 'skill_0'
          | 'skill_1'
          | 'skill_2'
          | 'skill_3'
          | 'skill_4',
        id: String(entry.id),
      };
  }
}

function buildTreemapChildren(
  data: BreakdownSingleDataSet['data'] | SingleDataSet['data']
): TreeMapData['children'] {
  const isSegmented =
    data.length > 0 && data[0] !== undefined && 'breakdown' in data[0];
  const breakdownData = data as BreakdownSingleDataSet['data'];
  const singleEntity =
    breakdownData.length === 1 ? breakdownData[0] : undefined;

  if (singleEntity !== undefined && singleEntity !== null && isSegmented) {
    return singleEntity.breakdown.map((b) => ({
      name: b.dim,
      value: b.value ?? 0,
      id: b.id ?? undefined,
    }));
  }

  if (isSegmented) {
    return breakdownData.map((entity) => ({
      name: entity.dim,
      value: entity.breakdown.reduce(
        (sum: number, b: SingleDataPoint) => sum + (b.value ?? 0),
        0
      ),
      children: entity.breakdown.map((b) => ({
        name: b.dim,
        value: b.value ?? 0,
        parentName: entity.dim,
        id: b.id ?? undefined,
      })),
    }));
  }

  return (data as SingleDataSet['data']).map((entity) => ({
    name: entity.dim,
    value: entity.value ?? 0,
  }));
}

function buildFlatDrilldownData(
  children: TreeMapData['children']
): TreeMapData {
  return { name: 'root', children };
}

type DrilledDownTreeMapContentProps = {
  compositionData: NonNullable<CompositionV2DataQuery['composition_v2']>;
  secondaryDimension: TreemapDrilldownSegment;
  drilldown: DrilldownFilter;
  drilldownStack: DrilldownEntry[];
  metricToUse: MetricValues | undefined;
  format: ReturnType<typeof getFormat>;
  treemapSizeTransform?: 'none' | 'sqrt';
  colorPalette: string[] | undefined;
  colorOverride: { Other: string } | undefined;
  onCellClick: (cellId: string, cellName: string, parentName?: string) => void;
  onHeaderClick: (targetDepth: number) => void;
};

function DrilledDownTreeMapContent({
  compositionData,
  secondaryDimension,
  drilldown,
  drilldownStack,
  metricToUse,
  format,
  treemapSizeTransform,
  colorPalette,
  colorOverride,
  onCellClick,
  onHeaderClick,
}: DrilledDownTreeMapContentProps) {
  const isCompanyEntitiesView =
    secondaryDimension === 'rics' &&
    drilldown.segment === 'rics' &&
    (drilldown.level === 'rics_k400' || drilldown.level === 'company') &&
    compositionData.entities?.length > 0 &&
    compositionData.entities.every(
      (e) => e?.metadata?.type === 'DATA_KIND_COMPANY'
    );

  const drilldownChildren = React.useMemo(() => {
    const effectiveMetric = metricToUse ?? 'headcount';
    if (isCompanyEntitiesView) {
      const metricKey = effectiveMetric as keyof NonNullable<
        (typeof compositionData.entities)[0]['metrics']
      >;
      return compositionData.entities
        .map((entity) => {
          const metricData = entity.metrics?.[metricKey];
          const lastPoint = Array.isArray(metricData)
            ? metricData[metricData.length - 1]
            : undefined;
          const rawValue =
            lastPoint && typeof lastPoint === 'object' && 'value' in lastPoint
              ? (lastPoint as { value?: number }).value
              : undefined;
          const value =
            typeof rawValue === 'number' && Number.isFinite(rawValue)
              ? rawValue
              : 0;
          const name = entity.metadata?.short_name ?? '';
          if (!name) return null;
          return {
            name,
            value,
            id:
              entity.metadata?.id != null
                ? String(entity.metadata.id)
                : undefined,
          };
        })
        .filter((c): c is NonNullable<typeof c> => c !== null);
    }
    const drilldownDataSet = convertCompositionData({
      metric: effectiveMetric as Parameters<
        typeof convertCompositionData
      >[0]['metric'],
      segment: secondaryDimension,
      data: compositionData,
    });

    if (drilldownDataSet.type !== DataType.BREAKDOWN_SINGLE) {
      return null;
    }

    return buildTreemapChildren(
      drilldownDataSet.data as BreakdownSingleDataSet['data']
    );
  }, [isCompanyEntitiesView, compositionData, metricToUse, secondaryDimension]);

  if (drilldownChildren === null) return null;

  const flatData = buildFlatDrilldownData(drilldownChildren);
  const breadcrumbPath: BreadcrumbSegment[] = drilldownStack.map((e, i) => ({
    name: e.name,
    targetDepth: i,
  }));

  return (
    <SharableChart
      Component={ReplotsTreeMap}
      data={flatData}
      format={format}
      sizeTransform={treemapSizeTransform}
      colorPalette={colorPalette}
      colorOverride={colorOverride}
      onCellClick={onCellClick}
      onHeaderClick={onHeaderClick}
      breadcrumbPath={breadcrumbPath}
    />
  );
}

export const TreeMap = ({
  data,
  metric,
}: {
  data: BreakdownSingleDataSet['data'] | SingleDataSet['data'];
  metric?: MetricValues;
}) => {
  const metricFromPlot = usePlotMetric();
  const metricToUse = metric ?? metricFromPlot;
  const { item } = useChart();
  const { primaryFilter } = useTabContentConfig();
  const primaryDimension = primaryFilter?.id;
  const secondaryDimension = item?.plot?.secondaryDimension;
  const { config: pageConfig } = usePageConfig();
  const effectiveFilters = useEffectiveFilters();
  const isReport = pageConfig.type === PageType.REPORT;
  const colorPalette = isReport
    ? [getPrimaryColor()]
    : (item?.plot?.colorPalette ?? undefined);
  const colorOverride = isReport ? undefined : { Other: getOtherColor() };

  const format = item?.plot?.format ?? getFormat({ metric: metricToUse });
  const treemapSizeTransform = item?.plot?.treemap?.sizeTransform;

  const [drilldownStack, setDrilldownStack] = React.useState<DrilldownEntry[]>(
    []
  );

  const initialLevelOffset = isTreemapDrilldownSegment(secondaryDimension)
    ? getTreemapInitialLevelOffset(secondaryDimension, effectiveFilters)
    : 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  React.useEffect(() => {
    setDrilldownStack([]);
  }, [primaryDimension, secondaryDimension, initialLevelOffset]);

  const levelOrder = isTreemapDrilldownSegment(secondaryDimension)
    ? getTreemapDrilldownLevelOrder(secondaryDimension)
    : [];
  const currentLevelIndex = initialLevelOffset + drilldownStack.length;
  const currentLevel =
    levelOrder.length > 0 ? levelOrder[currentLevelIndex] : undefined;
  const isLastDrillLevel =
    levelOrder.length > 0 &&
    (initialLevelOffset + drilldownStack.length >= levelOrder.length - 1 ||
      (currentLevel !== undefined &&
        isTreemapDrilldownSegment(secondaryDimension) &&
        getTreemapDrilldownIsLeafLevel(secondaryDimension, currentLevel)));

  const handleCellClick = React.useCallback(
    (cellId: string, cellName: string, _parentName?: string) => {
      if (cellName.toLowerCase() === 'other') return;
      if (isLastDrillLevel) return;
      if (!isTreemapDrilldownSegment(secondaryDimension)) return;

      const levelOrder = getTreemapDrilldownLevelOrder(secondaryDimension);
      const currentLevel =
        levelOrder[initialLevelOffset + drilldownStack.length];
      if (currentLevel === undefined) return;
      if (
        initialLevelOffset + drilldownStack.length >= levelOrder.length - 1 ||
        getTreemapDrilldownIsLeafLevel(secondaryDimension, currentLevel)
      ) {
        return;
      }

      const needsNumericId =
        secondaryDimension === 'geography' || secondaryDimension === 'rics';
      const id = needsNumericId ? parseInt(cellId, 10) : cellId;
      if (needsNumericId && !Number.isFinite(id as number)) return;

      setDrilldownStack((prev) => [
        ...prev,
        {
          level: currentLevel,
          id,
          name: cellName,
          segment: secondaryDimension,
        },
      ]);
    },
    [
      isLastDrillLevel,
      secondaryDimension,
      drilldownStack.length,
      initialLevelOffset,
    ]
  );

  const children = React.useMemo(() => buildTreemapChildren(data), [data]);
  const asTree: TreeMapData = React.useMemo(
    () => ({ name: 'Root', children }),
    [children]
  );

  const handleHeaderClick = React.useCallback((targetDepth: number) => {
    setDrilldownStack((prev) => prev.slice(0, targetDepth));
  }, []);

  if (
    drilldownStack.length > 0 &&
    isTreemapDrilldownSegment(secondaryDimension) &&
    primaryDimension !== undefined
  ) {
    const topOfStack = drilldownStack[drilldownStack.length - 1];
    if (topOfStack === undefined) return null;

    const drilldown = drilldownEntryToFilter(topOfStack);

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            width: '100%',
          }}
        >
          <CompositionTreemapDrilldownProvider
            primaryDimension={primaryDimension}
            drilldown={drilldown}
          >
            {({ data: drilldownData, status }) => {
              if (status === DataProviderQueryStatus.Fetching) {
                return (
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      minHeight: 0,
                    }}
                  >
                    <ChartLoader />
                  </div>
                );
              }
              const compositionData = drilldownData?.composition_v2;
              if (compositionData === undefined || compositionData === null) {
                return null;
              }

              return (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <DrilledDownTreeMapContent
                    compositionData={compositionData}
                    secondaryDimension={secondaryDimension}
                    drilldown={drilldown}
                    drilldownStack={drilldownStack}
                    metricToUse={metricToUse}
                    format={format}
                    treemapSizeTransform={treemapSizeTransform}
                    colorPalette={colorPalette}
                    colorOverride={colorOverride}
                    onCellClick={handleCellClick}
                    onHeaderClick={handleHeaderClick}
                  />
                </div>
              );
            }}
          </CompositionTreemapDrilldownProvider>
        </div>
      </div>
    );
  }

  return (
    <SharableChart
      Component={ReplotsTreeMap}
      data={asTree}
      format={format}
      sizeTransform={treemapSizeTransform}
      colorPalette={colorPalette}
      colorOverride={colorOverride}
      onCellClick={
        isTreemapDrilldownSegment(secondaryDimension)
          ? handleCellClick
          : undefined
      }
    />
  );
};
